import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import ProgramEditorTabs from '../ProgramEditorTabs';
import { Plus, Trash2, Settings2, Pencil } from 'lucide-react';
import ImageSourceField from '../admin/ImageSourceField';
import { pickDefaultCycleId } from '../../utils/cycleUtils';

const emptyForm = { name: '', color: '#10b981', active_cycle_id: '', active: true, descripcion: '', image_url: '' };

/** Programas propios del gimnasio virtual (independientes de D28D plataforma). */
export default function GymMastersPrograms({ onBack, scopeGymId = null }) {
  const { t } = useI18n();
  const [programs, setPrograms] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [configuringProgram, setConfiguringProgram] = useState(null);
  const [provisioning, setProvisioning] = useState(false);

  const provisionMasters = async () => {
    if (!scopeGymId) return;
    setProvisioning(true);
    setError('');
    setMessage('');
    try {
      const res = await api.post(`/gyms/${scopeGymId}/provision-masters`);
      const data = res.data?.data;
      if (data?.skipped && data?.reason === 'already_provisioned') {
        setMessage(t('gym.programs.already_ready', 'La estructura maestra ya está lista.'));
      } else {
        setMessage(t('gym.programs.masters_ready', 'Estructura creada: ciclo + programa listos. Configura la cuenta Zoom en Configurar → Zoom.'));
      }
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || t('gym.programs.provision_error', 'No se pudo inicializar la estructura maestra'));
    } finally {
      setProvisioning(false);
    }
  };

  const fetchPrograms = useCallback(async () => {
    if (!scopeGymId) {
      setPrograms([]);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/programs', { params: { gym_id: scopeGymId, gym_only: 'true' } });
      setPrograms(res.data?.data || []);
    } catch {
      setError(t('gym.programs.error_loading', 'Error cargando programas'));
    }
  }, [scopeGymId, t]);

  const fetchCycles = useCallback(async () => {
    if (!scopeGymId) {
      setCycles([]);
      return;
    }
    try {
      const res = await api.get('/cycles', { params: { gym_id: scopeGymId, gym_only: 'true' } });
      setCycles(res.data?.data || []);
    } catch {
      setCycles([]);
    }
  }, [scopeGymId]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchPrograms(), fetchCycles()]);
    setLoading(false);
  }, [fetchPrograms, fetchCycles]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const resetForm = () => {
    setForm({ ...emptyForm, active_cycle_id: pickDefaultCycleId(cycles) || (cycles[0]?.id ? String(cycles[0].id) : '') });
    setEditingId(null);
    setShowForm(false);
  };

  const openConfigure = (program) => {
    setConfiguringProgram(program);
    setShowForm(false);
    setEditingId(null);
    setMessage('');
    setError('');
  };

  const startEdit = (program) => {
    setConfiguringProgram(null);
    setEditingId(program.id);
    setForm({
      name: program.name || '',
      color: program.color || '#10b981',
      active_cycle_id: program.active_cycle_id ? String(program.active_cycle_id) : '',
      active: program.active !== false,
      descripcion: program.descripcion || '',
      image_url: program.image_url || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!form.name.trim()) {
      setError(t('gym.programs.name_required', 'El nombre del programa es obligatorio'));
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        color: form.color,
        active: form.active,
        active_cycle_id: form.active_cycle_id ? Number(form.active_cycle_id) : undefined,
        gym_id: scopeGymId,
      };
      if (editingId) {
        await api.put(`/programs/${editingId}`, payload);
        if (form.descripcion || form.image_url) {
          await api.put(`/programs/${editingId}`, {
            descripcion: form.descripcion?.trim() || '',
            image_url: form.image_url?.trim() || '',
          });
        }
        setMessage(t('gym.programs.updated', 'Programa actualizado'));
      } else {
        const res = await api.post('/programs', payload);
        const created = res.data?.data;
        if (created?.id && (form.descripcion?.trim() || form.image_url?.trim())) {
          await api.put(`/programs/${created.id}`, {
            descripcion: form.descripcion?.trim() || '',
            image_url: form.image_url?.trim() || '',
          });
        }
        setMessage(t('gym.programs.created', 'Programa creado'));
      }
      resetForm();
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || t('gym.programs.save_error', 'Error guardando programa'));
    }
  };

  const handleDelete = async (program) => {
    if (!window.confirm(t('gym.programs.delete_confirm', '¿Borrar este programa?'))) return;
    try {
      await api.delete(`/programs/${program.id}`);
      setMessage(t('gym.programs.deleted', 'Programa eliminado'));
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || t('gym.programs.delete_error', 'No se pudo borrar el programa'));
    }
  };

  return (
    <div className="dashboard-main-view space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {onBack ? (
            <button type="button" className="btn-secondary panel-back-btn mb-2" onClick={onBack}>
              {t('panel.back_panel', '← Panel')}
            </button>
          ) : null}
          <h2 className="d28d-page-title">{t('gym.masters.programs', 'Programas de tu gimnasio')}</h2>
          <p className="d28d-text-muted text-sm mt-1">
            {t('gym.masters.programs_desc', 'Crea programas propios de tu sede. Luego asígnalos al crear planes comerciales.')}
          </p>
        </div>
        {!showForm && (
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => {
              setForm({ ...emptyForm, active_cycle_id: pickDefaultCycleId(cycles) || (cycles[0]?.id ? String(cycles[0].id) : '') });
              setEditingId(null);
              setShowForm(true);
            }}
          >
            <Plus className="w-4 h-4" />
            {t('gym.programs.new', 'Nuevo programa')}
          </button>
        )}
      </header>

      {programs.length > 0 ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <p className="font-semibold mb-1">{t('gym.programs.own_notice_title', 'Programas propios de tu sede')}</p>
          <p className="m-0">
            {t(
              'gym.programs.own_notice',
              'Estos programas pertenecen solo a tu gimnasio virtual. Puedes editarlos sin afectar el catálogo de plataforma ni otros gimnasios.',
            )}
          </p>
        </div>
      ) : null}

      {cycles.length === 0 && !loading ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-3">
          <p>{t('gym.programs.need_cycles', 'Primero crea al menos un ciclo en Maestros → Ciclos para vincularlo al programa.')}</p>
          <button type="button" className="btn-primary text-sm" disabled={provisioning} onClick={provisionMasters}>
            {provisioning
              ? t('common.loading', 'Cargando…')
              : t('gym.programs.init_masters', 'Inicializar estructura (ciclo + programa)')}
          </button>
        </div>
      ) : null}

      {error ? <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-xl text-sm">{error}</div> : null}
      {message ? <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-xl text-sm">{message}</div> : null}

      {configuringProgram ? (
        <ProgramEditorTabs
          program={configuringProgram}
          cycles={cycles}
          gymId={scopeGymId}
          onClose={() => setConfiguringProgram(null)}
          onSaved={async () => {
            await loadAll();
            const res = await api.get('/programs', { params: { gym_id: scopeGymId, gym_only: 'true' } });
            const fresh = (res.data?.data || []).find((p) => p.id === configuringProgram.id);
            if (fresh) setConfiguringProgram(fresh);
          }}
        />
      ) : null}

      {showForm && (
        <form onSubmit={handleSubmit} className="ui-light-panel border border-slate-200 rounded-xl p-5 space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <label className="ui-field md:col-span-2">
              <span className="ui-field__label">{t('gym.programs.name', 'Nombre del programa')}</span>
              <input
                className="ui-input mt-1"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Fuerza Virtual"
                required
              />
            </label>
            <label className="ui-field">
              <span className="ui-field__label">{t('gym.programs.color', 'Color')}</span>
              <input className="ui-input mt-1 h-10" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
            </label>
          </div>
          <label className="ui-field block">
            <span className="ui-field__label">{t('gym.programs.description', 'Descripción')}</span>
            <textarea
              className="ui-input mt-1 min-h-[80px] w-full"
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder={t('gym.programs.description_placeholder', 'Describe el programa para tus usuarios…')}
            />
          </label>
          <ImageSourceField
            label={t('gym.programs.image', 'Imagen del programa')}
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            alt={form.name || 'Programa'}
          />
          <div className="grid gap-4 md:grid-cols-2">
            <label className="ui-field">
              <span className="ui-field__label">{t('gym.programs.active_cycle', 'Ciclo activo')}</span>
              <select
                className="ui-input mt-1"
                value={form.active_cycle_id}
                onChange={(e) => setForm({ ...form, active_cycle_id: e.target.value })}
                required
              >
                <option value="">{t('common.select', 'Seleccionar…')}</option>
                {cycles.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || `Ciclo ${c.id}`}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm pt-6">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              {t('gym.programs.active', 'Activo')}
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary">{editingId ? t('common.save', 'Guardar') : t('common.create', 'Crear')}</button>
            <button type="button" className="btn-secondary" onClick={resetForm}>{t('common.cancel', 'Cancelar')}</button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-stone-600">{t('common.loading', 'Cargando…')}</p>
      ) : programs.length === 0 ? (
        <div className="space-y-3">
          <p className="text-sm d28d-text-muted">{t('gym.programs.none', 'Aún no hay programas. Crea el primero para tu gimnasio.')}</p>
          <button type="button" className="btn-secondary text-sm" disabled={provisioning} onClick={provisionMasters}>
            {provisioning
              ? t('common.loading', 'Cargando…')
              : t('gym.programs.init_masters', 'Inicializar estructura (ciclo + programa)')}
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-stone-600">{t('gym.programs.name', 'Nombre')}</th>
                <th className="px-4 py-3 text-left font-semibold text-stone-600">{t('gym.programs.active_cycle', 'Ciclo activo')}</th>
                <th className="px-4 py-3 text-left font-semibold text-stone-600">{t('gym.programs.zoom', 'Zoom')}</th>
                <th className="px-4 py-3 text-left font-semibold text-stone-600">{t('common.status', 'Estado')}</th>
                <th className="px-4 py-3 text-right font-semibold text-stone-600">{t('common.actions', 'Acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {programs.map((p) => {
                const cycle = cycles.find((c) => Number(c.id) === Number(p.active_cycle_id));
                return (
                  <tr key={p.id} className="border-t border-stone-100">
                    <td className="px-4 py-3 font-medium text-stone-900">
                      <span className="inline-block w-3 h-3 rounded-full mr-2 align-middle" style={{ backgroundColor: p.color || '#64748b' }} />
                      {p.name}
                      <div className="text-xs text-stone-500 font-normal mt-0.5">{p.id}</div>
                    </td>
                    <td className="px-4 py-3 text-stone-700">{cycle?.name || p.active_cycle_id || '—'}</td>
                    <td className="px-4 py-3 text-stone-600 text-xs">
                      {p.zoom_email ? (
                        <span title={p.zoom_email}>{p.zoom_email}</span>
                      ) : (
                        <span className="text-amber-700">{t('gym.programs.zoom_pending', 'Sin configurar')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-stone-600">{p.active !== false ? t('common.active', 'Activo') : t('common.inactive', 'Inactivo')}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button type="button" className="btn-secondary text-xs" onClick={() => startEdit(p)}>
                          <Pencil className="w-3 h-3 inline" /> {t('common.edit', 'Editar')}
                        </button>
                        <button type="button" className="btn-primary text-xs" onClick={() => openConfigure(p)}>
                          <Settings2 className="w-3 h-3 inline" /> {t('gym.programs.configure', 'Configurar')}
                        </button>
                        <button type="button" className="btn-secondary text-xs text-red-700 border-red-200" onClick={() => handleDelete(p)}>
                          <Trash2 className="w-3 h-3 inline" /> {t('common.delete', 'Borrar')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
